//@ts-nocheck
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import _ from "lodash";
import moment from "moment-timezone";
import Select from "react-select";

import { Container, Button, Frame, Row, Dropdown } from "components/ui-kit/styled-templates";
import Table from "components/ui-kit/table";
import { Control } from "components/ui-kit/control";

import AdminApi, { CreateUniAdminPayload, UserInfo } from "api/admin-api";
import UserApi from "api/user-api";
import UniversityApi from "api/university-api";

import useBodyBackground from "hooks/useBodyBackground";
import { useStorageListener } from "hooks/useStorage";

import AlertsService from "services/alerts-service";

// Роли пользователей
const USER_ROLES = {
    "uni-admin": "Администратор ВУЗа",
    "clubs-admin": "Администратор клубов",
    "clubs-uni-admin": "Администратор клуба ВУЗа",
    "user": "Студент",
    "admin": "Администратор платформы"
};

const UsersManagementPage = () => {
    const { t, i18n } = useTranslation();
    const isRu = i18n.language === "ru";

    useBodyBackground("white");

    // Состояния
    const [activeTab, setActiveTab] = useState<"uni-admins" | "search" | "advanced-search">("uni-admins");
    const [uniAdmins, setUniAdmins] = useState<UserInfo[]>([]);
    const [loading, setLoading] = useState(false);

    // Модальное окно создания ВУЗа
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState<CreateUniAdminPayload>({
        email: "",
        details: {
            name: "",
            shortName: "",
            district: "",
            region: "",
            city: "",
            country: "РОССИЯ",
            adminContacts: ""
        }
    });

    // Поиск пользователя (вторая вкладка)
    const [searchEmail, setSearchEmail] = useState("");
    const [foundUser, setFoundUser] = useState<UserInfo | null>(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchPerformed, setSearchPerformed] = useState(false);

    // Фильтр для таблицы администраторов ВУЗов
    const [tableFilter, setTableFilter] = useState("");

    // Расширенный поиск (третья вкладка)
    const [advancedFilters, setAdvancedFilters] = useState({
        gender: "",
        city: "",
        ageFrom: "",
        ageTo: ""
    });
    const [advancedSearchResults, setAdvancedSearchResults] = useState<UserInfo[]>([]);
    const [advancedSearchLoading, setAdvancedSearchLoading] = useState(false);
    const [advancedSearchPerformed, setAdvancedSearchPerformed] = useState(false);

    // Модальное окно сгенерированного пароля
    const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

    // Модальное окно редактирования пользователя
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState<any>({});

    // Модальное окно смены email для администратора ВУЗа
    const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
    const [changeEmailUser, setChangeEmailUser] = useState<UserInfo | null>(null);
    const [newEmail, setNewEmail] = useState("");

    // Данные университетов
    const universities = useStorageListener("universities", {});

    // Загрузка списка администраторов ВУЗов
    const loadUniAdmins = async () => {
        setLoading(true);
        try {
            const admins = await AdminApi.getUniAdmins();
            setUniAdmins(admins);
        } catch (error) {
            console.error("Error loading uni admins:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUniAdmins();
        UniversityApi.getAllUniversitiesAdmin();
    }, []);

    // Обработчик создания ВУЗа
    const handleCreateUni = async () => {
        if (!createForm.email || !createForm.details.name) {
            AlertsService.showError("Заполните обязательные поля: Email и Название ВУЗа");
            return;
        }

        try {
            const result = await AdminApi.createUniAdmin(createForm);
            setShowCreateModal(false);
            setCreateForm({
                email: "",
                details: {
                    name: "",
                    shortName: "",
                    district: "",
                    region: "",
                    city: "",
                    country: "РОССИЯ",
                    adminContacts: ""
                }
            });

            // Показываем сгенерированный пароль, если он есть
            if (result?.password) {
                setGeneratedPassword(result.password);
            }

            // Перезагружаем список
            loadUniAdmins();
            UniversityApi.getAllUniversitiesAdmin();
        } catch (error) {
            console.error("Error creating uni admin:", error);
        }
    };

    // Поиск пользователя по email
    const handleSearchUser = async () => {
        if (!searchEmail.trim()) {
            AlertsService.showError("Введите email для поиска");
            return;
        }

        setSearchLoading(true);
        setSearchPerformed(true);
        try {
            const user = await AdminApi.findUserByEmail(searchEmail);
            setFoundUser(user);
        } catch (error) {
            console.error("Error searching user:", error);
            setFoundUser(null);
        } finally {
            setSearchLoading(false);
        }
    };

    // Расширенный поиск пользователей
    const handleAdvancedSearch = async () => {
        // Проверяем, что хотя бы один фильтр заполнен
        const hasFilters = Object.values(advancedFilters).some(v => v.trim() !== "");
        if (!hasFilters) {
            AlertsService.showError("Заполните хотя бы один фильтр для поиска");
            return;
        }

        // Проверка что ageFrom <= ageTo
        if (advancedFilters.ageFrom && advancedFilters.ageTo) {
            if (parseInt(advancedFilters.ageFrom) > parseInt(advancedFilters.ageTo)) {
                AlertsService.showError("Возраст 'от' не может быть больше возраста 'до'");
                return;
            }
        }

        setAdvancedSearchLoading(true);
        setAdvancedSearchPerformed(true);
        try {
            // Убираем пустые значения из фильтров
            const cleanFilters = Object.fromEntries(
                Object.entries(advancedFilters).filter(([_, v]) => v.trim() !== "")
            );
            const users = await AdminApi.findUserByFilters(cleanFilters);
            setAdvancedSearchResults(users || []);
        } catch (error) {
            console.error("Error in advanced search:", error);
            setAdvancedSearchResults([]);
        } finally {
            setAdvancedSearchLoading(false);
        }
    };

    // Сброс фильтров расширенного поиска
    const handleResetAdvancedFilters = () => {
        setAdvancedFilters({
            gender: "",
            city: "",
            ageFrom: "",
            ageTo: ""
        });
        setAdvancedSearchResults([]);
        setAdvancedSearchPerformed(false);
    };

    // Действия с пользователем
    const handleBlockUser = async (userId: string, isBlocked: boolean) => {
        try {
            if (isBlocked) {
                await AdminApi.unblockUser(userId);
            } else {
                await AdminApi.blockUser(userId);
            }
            // Обновляем данные
            if (activeTab === "uni-admins") {
                loadUniAdmins();
            } else if (foundUser) {
                handleSearchUser();
            }
        } catch (error) {
            console.error("Error blocking/unblocking user:", error);
        }
    };

    const handleGeneratePassword = async (userId: string) => {
        try {
            const result = await AdminApi.generatePassword(userId);
            setGeneratedPassword(result.password);
        } catch (error) {
            console.error("Error generating password:", error);
        }
    };

    const handleDeleteUser = async (userId: string, isDeleted: boolean) => {
        const action = isDeleted ? "восстановить" : "удалить";
        if (!window.confirm(`Вы уверены, что хотите ${action} этого пользователя?`)) {
            return;
        }

        try {
            if (isDeleted) {
                await AdminApi.undeleteUser(userId);
            } else {
                await AdminApi.deleteUser(userId);
            }
            // Обновляем данные
            if (activeTab === "uni-admins") {
                loadUniAdmins();
            } else if (foundUser) {
                handleSearchUser();
            }
        } catch (error) {
            console.error("Error deleting/undeleting user:", error);
        }
    };

    const handleLoginAsUni = async (universityId: string) => {
        try {
            const token = await UserApi.getUniToken(universityId);
            window.open(`${window.location.origin}/?auth_token=${token}`, "_blank");
        } catch (error) {
            console.error("Error getting uni token:", error);
        }
    };

    // Сброс пароля администратора ВУЗа
    const handleResetPassword = async (userId: string) => {
        if (!window.confirm("Вы уверены, что хотите сбросить пароль этого пользователя?")) {
            return;
        }
        try {
            const result = await AdminApi.resetPassword(userId);
            setGeneratedPassword(result.password);
        } catch (error) {
            console.error("Error resetting password:", error);
        }
    };

    // Открытие модала смены email
    const openChangeEmailModal = (user: UserInfo) => {
        setChangeEmailUser(user);
        setNewEmail(user.email || "");
        setShowChangeEmailModal(true);
    };

    // Смена email администратора ВУЗа
    const handleChangeEmail = async () => {
        if (!changeEmailUser || !newEmail.trim()) {
            AlertsService.showError("Введите новый email");
            return;
        }
        try {
            await AdminApi.setUserEmail(changeEmailUser.id, newEmail.trim());
            setShowChangeEmailModal(false);
            setChangeEmailUser(null);
            setNewEmail("");
            // Перезагружаем список
            loadUniAdmins();
        } catch (error) {
            console.error("Error changing email:", error);
        }
    };

    // Открытие модала редактирования
    const openEditModal = (user: UserInfo) => {
        setEditForm({
            id: user.id,
            name: user.name || "",
            studentRoleType: (user as any).studentRoleType || "",
            gender: (user as any).gender || "",
            birthDate: (user as any).birthDate || (user as any).birthday || "",
            city: (user as any).city || (user as any).departureCity || "",
            phone: user.phone || "",
            socialLink: (user as any).socialLink || (user as any).socialUrl || "",
            universityId: user.universityId || ""
        });
        setShowEditModal(true);
    };

    // Сохранение изменений пользователя
    const handleSaveUser = async () => {
        try {
            await AdminApi.updateUser(editForm.id, editForm);
            setShowEditModal(false);
            // Обновляем данные найденного пользователя
            if (foundUser) {
                handleSearchUser();
            }
        } catch (error) {
            console.error("Error updating user:", error);
        }
    };

    // Опции для react-select (университеты)
    const universityOptions = useMemo(() => {
        return Object.values(universities).map((uni: any) => ({
            value: uni.id,
            label: uni.details?.shortName || uni.details?.name || uni.id
        }));
    }, [universities]);

    // Опции для studentRoleType
    const studentRoleOptions = [
        { value: "student", label: "Студент" },
        { value: "youngSpecialist", label: "Молодой специалист" },
        { value: "youngScientist", label: "Молодой ученый" }
    ];

    // Подготовка данных для таблицы
    // Ищем университет по university.userId === admin.id
    const universitiesArray = _.values(universities);

    const tableRows = uniAdmins
        .map((admin, index) => {
            // Ищем университет, где userId совпадает с id админа
            const university = universitiesArray.find((uni: any) => uni.userId === admin.id);

            return {
                ...admin,
                number: index + 1,
                universityId: university?.id,
                universityName: university?.details?.name || university?.details?.shortName || "-",
                universityCity: university?.details?.city || "-",
                universityRegion: university?.details?.region || "-",
                roleName: USER_ROLES[admin.userRole] || admin.userRole,
                statusText: admin.blocked ? "Заблокирован" : "Активен",
                createdDate: admin.createdAt ? moment(admin.createdAt).format("DD.MM.YYYY") : "-"
            };
        })
        .filter((row) => {
            // Фильтрация по email или названию ВУЗа
            if (!tableFilter.trim()) return true;
            const filterLower = tableFilter.toLowerCase().trim();
            return (
                row.email?.toLowerCase().includes(filterLower) ||
                row.universityName?.toLowerCase().includes(filterLower)
            );
        });

    return (
        <Container extra="padding-top: 10px; align-items: flex-start;">
            <PageHeader>
                <h2>Управление пользователями</h2>
            </PageHeader>

            {/* Табы */}
            <TabsWrapper>
                <Tab
                    active={activeTab === "uni-admins"}
                    onClick={() => setActiveTab("uni-admins")}
                >
                    Администраторы ВУЗов
                </Tab>
                <Tab
                    active={activeTab === "search"}
                    onClick={() => setActiveTab("search")}
                >
                    Поиск пользователя
                </Tab>
                <Tab
                    active={activeTab === "advanced-search"}
                    onClick={() => setActiveTab("advanced-search")}
                >
                    Расширенный поиск
                </Tab>
            </TabsWrapper>

            {/* Контент табов */}
            {activeTab === "uni-admins" && (
                <TabContent>
                    <ActionsRow>
                        <Button onClick={() => setShowCreateModal(true)}>
                            + Добавить ВУЗ
                        </Button>
                        <TableFilterInput
                            type="text"
                            placeholder="Поиск по email или названию ВУЗа..."
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                        />
                    </ActionsRow>

                    <Table
                        name="uni-admins-table"
                        withPagination={true}
                        useBackendProcessing={false}
                        paginationOptions={[50, 100, 200]}
                        rows={tableRows}
                        sortable={true}
                        columns={[
                            { name: "number", label: "№", extra: "flex: 0.3;" },
                            { name: "email", label: "Email", extra: "flex: 1.5;" },
                            {
                                name: "universityName",
                                label: "Название ВУЗа",
                                extra: "flex: 2.5;",
                            },
                            { name: "universityCity", label: "Город", extra: "flex: 1;" },
                            { name: "universityRegion", label: "Регион", extra: "flex: 1;" },
                            {
                                name: "statusText",
                                label: "Статус",
                                extra: "flex: 0.8;",
                                transform: ({ row }) => row.blocked ? "Заблокирован" : "Активен"
                            },
                            {
                                name: "actions",
                                label: "",
                                extra: "flex: 0.5; justify-content: flex-end;",
                                sortable: false,
                                cell: {
                                    type: "menu",
                                    options: [
                                        {
                                            label: "Войти как ВУЗ",
                                            onClick: ({ row }) => handleLoginAsUni(row.universityId)
                                        },
                                        {
                                            label: "Сбросить пароль",
                                            onClick: ({ row }) => handleResetPassword(row.id)
                                        },
                                        {
                                            label: "Изменить email",
                                            onClick: ({ row }) => openChangeEmailModal(row)
                                        }
                                    ]
                                }
                            }
                        ]}
                    />
                </TabContent>
            )}

            {activeTab === "search" && (
                <TabContent>
                    <SearchSection>
                        <SearchRow>
                            <SearchInput
                                type="email"
                                placeholder="Введите email пользователя"
                                value={searchEmail}
                                onChange={(e) => setSearchEmail(e.target.value)}
                                onKeyPress={(e) => e.key === "Enter" && handleSearchUser()}
                            />
                            <Button onClick={handleSearchUser} disabled={searchLoading}>
                                {searchLoading ? "Поиск..." : "Найти"}
                            </Button>
                        </SearchRow>

                        {searchPerformed && !searchLoading && (
                            foundUser ? (
                                <UserCard>
                                    <UserCardHeader>
                                        <h3>Найденный пользователь</h3>
                                        <StatusBadgesWrapper>
                                            {foundUser.isDeleted && (
                                                <UserStatus deleted>Удалён</UserStatus>
                                            )}
                                            <UserStatus blocked={foundUser.blocked || foundUser.isBlocked}>
                                                {(foundUser.blocked || foundUser.isBlocked) ? "Заблокирован" : "Активен"}
                                            </UserStatus>
                                        </StatusBadgesWrapper>
                                    </UserCardHeader>

                                    <UserInfoGrid>
                                        <UserInfoItem>
                                            <UserInfoLabel>ID:</UserInfoLabel>
                                            <UserInfoValue>{foundUser.id}</UserInfoValue>
                                        </UserInfoItem>
                                        <UserInfoItem>
                                            <UserInfoLabel>Email:</UserInfoLabel>
                                            <UserInfoValue>{foundUser.email}</UserInfoValue>
                                        </UserInfoItem>
                                        <UserInfoItem>
                                            <UserInfoLabel>Роль:</UserInfoLabel>
                                            <UserInfoValue>{USER_ROLES[foundUser.userRole] || foundUser.userRole}</UserInfoValue>
                                        </UserInfoItem>
                                        {foundUser.name && (
                                            <UserInfoItem>
                                                <UserInfoLabel>Имя:</UserInfoLabel>
                                                <UserInfoValue>{foundUser.name}</UserInfoValue>
                                            </UserInfoItem>
                                        )}
                                        {foundUser.phone && (
                                            <UserInfoItem>
                                                <UserInfoLabel>Телефон:</UserInfoLabel>
                                                <UserInfoValue>{foundUser.phone}</UserInfoValue>
                                            </UserInfoItem>
                                        )}
                                        {foundUser.universityId && (
                                            <UserInfoItem>
                                                <UserInfoLabel>ID ВУЗа:</UserInfoLabel>
                                                <UserInfoValue>{foundUser.universityId}</UserInfoValue>
                                            </UserInfoItem>
                                        )}
                                        {foundUser.university?.details?.name && (
                                            <UserInfoItem>
                                                <UserInfoLabel>ВУЗ:</UserInfoLabel>
                                                <UserInfoValue>{foundUser.university.details.name}</UserInfoValue>
                                            </UserInfoItem>
                                        )}
                                    </UserInfoGrid>

                                    <UserActions>
                                        <Button
                                            variant="outlined"
                                            onClick={() => openEditModal(foundUser)}
                                        >
                                            Редактировать
                                        </Button>
                                        {foundUser.universityId && (
                                            <Button
                                                variant="outlined"
                                                onClick={() => handleLoginAsUni(foundUser.universityId)}
                                            >
                                                Войти как ВУЗ
                                            </Button>
                                        )}
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleBlockUser(foundUser.id, foundUser.blocked || foundUser.isBlocked)}
                                        >
                                            {(foundUser.blocked || foundUser.isBlocked) ? "Разблокировать" : "Заблокировать"}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            extra={foundUser.isDeleted ? "color: #2E7D32; border-color: #2E7D32;" : "color: red; border-color: red;"}
                                            onClick={() => handleDeleteUser(foundUser.id, foundUser.isDeleted)}
                                        >
                                            {foundUser.isDeleted ? "Восстановить" : "Удалить"}
                                        </Button>
                                    </UserActions>
                                </UserCard>
                            ) : (
                                <NotFoundMessage>
                                    Пользователь с email "{searchEmail}" не найден
                                </NotFoundMessage>
                            )
                        )}
                    </SearchSection>
                </TabContent>
            )}

            {activeTab === "advanced-search" && (
                <TabContent>
                    <AdvancedSearchSection>
                        <AdvancedFiltersGrid>
                            <FormGroup>
                                <FormLabel>Пол</FormLabel>
                                <FormSelect
                                    value={advancedFilters.gender}
                                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, gender: e.target.value })}
                                >
                                    <option value="">Любой</option>
                                    <option value="male">Мужской</option>
                                    <option value="female">Женский</option>
                                </FormSelect>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>Город</FormLabel>
                                <FormInput
                                    value={advancedFilters.city}
                                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, city: e.target.value })}
                                    placeholder="Москва"
                                />
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>Возраст от</FormLabel>
                                <FormSelect
                                    value={advancedFilters.ageFrom}
                                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, ageFrom: e.target.value })}
                                >
                                    <option value="">Не выбрано</option>
                                    {Array.from({ length: 53 }, (_, i) => i + 18).map(age => (
                                        <option key={age} value={age}>{age}</option>
                                    ))}
                                </FormSelect>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>Возраст до</FormLabel>
                                <FormSelect
                                    value={advancedFilters.ageTo}
                                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, ageTo: e.target.value })}
                                >
                                    <option value="">Не выбрано</option>
                                    {Array.from({ length: 53 }, (_, i) => i + 18).map(age => (
                                        <option key={age} value={age}>{age}</option>
                                    ))}
                                </FormSelect>
                            </FormGroup>
                        </AdvancedFiltersGrid>

                        <AdvancedSearchActions>
                            <Button onClick={handleAdvancedSearch} disabled={advancedSearchLoading}>
                                {advancedSearchLoading ? "Поиск..." : "Найти"}
                            </Button>
                            <Button variant="outlined" onClick={handleResetAdvancedFilters}>
                                Сбросить фильтры
                            </Button>
                        </AdvancedSearchActions>

                        {advancedSearchPerformed && !advancedSearchLoading && (
                            advancedSearchResults.length > 0 ? (
                                <AdvancedSearchResults>
                                    <h3>Найдено пользователей: {advancedSearchResults.length}</h3>
                                    <Table
                                        name="advanced-search-results-table"
                                        withPagination={true}
                                        useBackendProcessing={false}
                                        paginationOptions={[20, 50, 100]}
                                        rows={advancedSearchResults.map((user, index) => ({
                                            ...user,
                                            number: index + 1,
                                            roleName: USER_ROLES[user.userRole] || user.userRole,
                                            universityName: user.university?.details?.shortName || user.university?.details?.name || "-",
                                            statusText: user.blocked ? "Заблокирован" : "Активен"
                                        }))}
                                        sortable={true}
                                        columns={[
                                            { name: "number", label: "№", extra: "flex: 0.3;" },
                                            { name: "email", label: "Email", extra: "flex: 1.5;" },
                                            { name: "name", label: "ФИО", extra: "flex: 1.5;" },
                                            { name: "phone", label: "Телефон", extra: "flex: 1;" },
                                            { name: "roleName", label: "Роль", extra: "flex: 1;" },
                                            { name: "universityName", label: "ВУЗ", extra: "flex: 1.5;" },
                                            {
                                                name: "statusText",
                                                label: "Статус",
                                                extra: "flex: 0.8;",
                                            },
                                            {
                                                name: "actions",
                                                label: "",
                                                extra: "flex: 0.5; justify-content: flex-end;",
                                                sortable: false,
                                                cell: {
                                                    type: "menu",
                                                    options: [
                                                        {
                                                            label: "Редактировать",
                                                            onClick: ({ row }) => openEditModal(row)
                                                        },
                                                        {
                                                            label: ({ row }) => (row?.blocked || row?.isBlocked) ? "Разблокировать" : "Заблокировать",
                                                            onClick: ({ row }) => handleBlockUser(row.id, row?.blocked || row?.isBlocked)
                                                        },
                                                        {
                                                            label: "Сгенерировать пароль",
                                                            onClick: ({ row }) => handleGeneratePassword(row.id)
                                                        }
                                                    ]
                                                }
                                            }
                                        ]}
                                    />
                                </AdvancedSearchResults>
                            ) : (
                                <NotFoundMessage>
                                    Пользователи не найдены по указанным фильтрам
                                </NotFoundMessage>
                            )
                        )}
                    </AdvancedSearchSection>
                </TabContent>
            )}

            {/* Модальное окно создания ВУЗа */}
            {showCreateModal && (
                <ModalOverlay onClick={() => setShowCreateModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>Создание ВУЗа и администратора</h3>
                            <CloseButton onClick={() => setShowCreateModal(false)}>×</CloseButton>
                        </ModalHeader>

                        <ModalBody>
                            <FormGroup>
                                <FormLabel>Email администратора *</FormLabel>
                                <FormInput
                                    type="email"
                                    value={createForm.email}
                                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                                    placeholder="admin@university.ru"
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Полное название ВУЗа *</FormLabel>
                                <FormInput
                                    value={createForm.details.name}
                                    onChange={(e) => setCreateForm({
                                        ...createForm,
                                        details: { ...createForm.details, name: e.target.value }
                                    })}
                                    placeholder='ФГБОУ ВО "Название университета"'
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Сокращенное название</FormLabel>
                                <FormInput
                                    value={createForm.details.shortName}
                                    onChange={(e) => setCreateForm({
                                        ...createForm,
                                        details: { ...createForm.details, shortName: e.target.value }
                                    })}
                                    placeholder="НУ"
                                />
                            </FormGroup>
                        </ModalBody>

                        <ModalFooter>
                            <Button variant="outlined" onClick={() => setShowCreateModal(false)}>
                                Отмена
                            </Button>
                            <Button onClick={handleCreateUni}>
                                Создать
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* Модальное окно с сгенерированным паролем */}
            {generatedPassword && (
                <ModalOverlay onClick={() => setGeneratedPassword(null)}>
                    <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: "620px" }}>
                        <ModalHeader>
                            <h3>Сгенерированный пароль</h3>
                            <CloseButton onClick={() => setGeneratedPassword(null)}>×</CloseButton>
                        </ModalHeader>

                        <ModalBody>
                            <PasswordDisplay>
                                <PasswordText>{generatedPassword}</PasswordText>
                                <CopyButton onClick={() => {
                                    navigator.clipboard.writeText(generatedPassword);
                                    AlertsService.showSuccess("Пароль скопирован в буфер обмена");
                                }}>
                                    Копировать
                                </CopyButton>
                            </PasswordDisplay>
                            <PasswordNote>
                                Сохраните этот пароль и передайте его пользователю.
                                После закрытия этого окна пароль больше не будет доступен.
                            </PasswordNote>
                        </ModalBody>

                        <ModalFooter>
                            <Button onClick={() => setGeneratedPassword(null)}>
                                Закрыть
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* Модальное окно редактирования пользователя */}
            {showEditModal && (
                <ModalOverlay onClick={() => setShowEditModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px" }}>
                        <ModalHeader>
                            <h3>Редактирование пользователя</h3>
                            <CloseButton onClick={() => setShowEditModal(false)}>×</CloseButton>
                        </ModalHeader>

                        <ModalBody>
                            <FormGroup>
                                <FormLabel>ФИО</FormLabel>
                                <FormInput
                                    value={editForm.name}
                                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                    placeholder="Иванов Иван Иванович"
                                />
                            </FormGroup>

                            <FormRow>
                                <FormGroup>
                                    <FormLabel>Тип</FormLabel>
                                    <FormSelect
                                        value={editForm.studentRoleType}
                                        onChange={(e) => setEditForm({ ...editForm, studentRoleType: e.target.value })}
                                    >
                                        <option value="">Не указан</option>
                                        <option value="student">Студент</option>
                                        <option value="youngSpecialist">Молодой специалист</option>
                                        <option value="youngScientist">Молодой ученый</option>
                                    </FormSelect>
                                </FormGroup>
                                <FormGroup>
                                    <FormLabel>Пол</FormLabel>
                                    <FormSelect
                                        value={editForm.gender}
                                        onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                                    >
                                        <option value="">Не указан</option>
                                        <option value="male">Мужской</option>
                                        <option value="female">Женский</option>
                                    </FormSelect>
                                </FormGroup>
                            </FormRow>

                            <FormRow>
                                <FormGroup>
                                    <FormLabel>Дата рождения</FormLabel>
                                    <FormInput
                                        type="date"
                                        value={editForm.birthDate}
                                        onChange={(e) => setEditForm({ ...editForm, birthDate: e.target.value })}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <FormLabel>Город отправления</FormLabel>
                                    <FormInput
                                        value={editForm.city}
                                        onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                                        placeholder="Москва"
                                    />
                                </FormGroup>
                            </FormRow>

                            <FormRow>
                                <FormGroup>
                                    <FormLabel>Телефон</FormLabel>
                                    <FormInput
                                        value={editForm.phone}
                                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                        placeholder="+7 (999) 123-45-67"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <FormLabel>Ссылка на соцсеть</FormLabel>
                                    <FormInput
                                        value={editForm.socialLink}
                                        onChange={(e) => setEditForm({ ...editForm, socialLink: e.target.value })}
                                        placeholder="https://vk.com/username"
                                    />
                                </FormGroup>
                            </FormRow>

                            <FormGroup>
                                <FormLabel>ВУЗ</FormLabel>
                                <Select
                                    value={universityOptions.find(opt => opt.value === editForm.universityId) || null}
                                    onChange={(option) => setEditForm({ ...editForm, universityId: option?.value || "" })}
                                    options={universityOptions}
                                    placeholder="Выберите ВУЗ..."
                                    isClearable
                                    isSearchable
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '42px',
                                            borderRadius: '8px',
                                            borderColor: '#E0E0E0',
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                    }}
                                    noOptionsMessage={() => "Ничего не найдено"}
                                />
                            </FormGroup>
                        </ModalBody>

                        <ModalFooter>
                            <Button variant="outlined" onClick={() => setShowEditModal(false)}>
                                Отмена
                            </Button>
                            <Button onClick={handleSaveUser}>
                                Сохранить
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* Модальное окно смены email */}
            {showChangeEmailModal && changeEmailUser && (
                <ModalOverlay onClick={() => setShowChangeEmailModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: "450px" }}>
                        <ModalHeader>
                            <h3>Изменить email</h3>
                            <CloseButton onClick={() => setShowChangeEmailModal(false)}>×</CloseButton>
                        </ModalHeader>

                        <ModalBody>
                            <FormGroup>
                                <FormLabel>Текущий email:</FormLabel>
                                <div style={{ padding: "10px 0", color: "#666" }}>{changeEmailUser.email}</div>
                            </FormGroup>

                            <FormGroup>
                                <FormLabel>Новый email</FormLabel>
                                <FormInput
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    placeholder="Введите новый email"
                                />
                            </FormGroup>
                        </ModalBody>

                        <ModalFooter>
                            <Button variant="outlined" onClick={() => setShowChangeEmailModal(false)}>
                                Отмена
                            </Button>
                            <Button onClick={handleChangeEmail}>
                                Сохранить
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Container>
    );
};

// Styled Components
const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 20px;

    h2 {
        margin: 0;
    }
`;

const TabsWrapper = styled.div`
    display: flex;
    gap: 0;
    border-bottom: 2px solid #E0E0E0;
    width: 100%;
    margin-bottom: 20px;
`;

const Tab = styled.div<{ active: boolean }>`
    padding: 12px 24px;
    cursor: pointer;
    font-weight: ${({ active }) => active ? "600" : "400"};
    color: ${({ active }) => active ? "#2196F3" : "#666"};
    border-bottom: 2px solid ${({ active }) => active ? "#2196F3" : "transparent"};
    margin-bottom: -2px;
    transition: all 0.2s;

    &:hover {
        color: #2196F3;
    }
`;

const TabContent = styled.div`
    width: 100%;
`;

const ActionsRow = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    align-items: center;
`;

const TableFilterInput = styled.input`
    padding: 10px 16px;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    min-width: 300px;
    margin-left: auto;

    &:focus {
        border-color: #2196F3;
    }

    &::placeholder {
        color: #999;
    }
`;

const SearchSection = styled.div`
    width: 100%;
`;

const SearchRow = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    max-width: 500px;
`;

const SearchInput = styled.input`
    flex: 1;
    padding: 10px 16px;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    font-size: 14px;
    outline: none;

    &:focus {
        border-color: #2196F3;
    }
`;

const UserCard = styled.div`
    background: #F5F5F5;
    border-radius: 12px;
    padding: 24px;
    max-width: 600px;
`;

const UserCardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
        margin: 0;
    }
`;

const StatusBadgesWrapper = styled.div`
    display: flex;
    gap: 8px;
`;

const UserStatus = styled.span<{ blocked?: boolean; deleted?: boolean }>`
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    background: ${({ blocked, deleted }) =>
        deleted ? "#F3E5F5" :
        blocked ? "#FFEBEE" : "#E8F5E9"};
    color: ${({ blocked, deleted }) =>
        deleted ? "#7B1FA2" :
        blocked ? "#C62828" : "#2E7D32"};
`;

const UserInfoGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 20px;
`;

const UserInfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const UserInfoLabel = styled.span`
    font-size: 12px;
    color: #666;
`;

const UserInfoValue = styled.span`
    font-size: 14px;
    font-weight: 500;
    word-break: break-all;
`;

const UserActions = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const NotFoundMessage = styled.div`
    padding: 20px;
    background: #FFF3E0;
    border-radius: 8px;
    color: #E65100;
    max-width: 500px;
`;

// Modal styles
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 12px;
    max-width: 600px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #E0E0E0;

    h3 {
        margin: 0;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    padding: 0;
    line-height: 1;

    &:hover {
        color: #333;
    }
`;

const ModalBody = styled.div`
    padding: 24px;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #E0E0E0;
`;

const FormGroup = styled.div`
    margin-bottom: 16px;
    flex: 1;
`;

const FormRow = styled.div`
    display: flex;
    gap: 16px;
`;

const FormLabel = styled.label`
    display: block;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 6px;
    color: #333;
`;

const FormInput = styled.input`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;

    &:focus {
        border-color: #2196F3;
    }
`;

const FormSelect = styled.select`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;
    background: white;
    cursor: pointer;

    &:focus {
        border-color: #2196F3;
    }
`;

const FormTextarea = styled.textarea`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    resize: vertical;
    box-sizing: border-box;

    &:focus {
        border-color: #2196F3;
    }
`;

const PasswordDisplay = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background: #F5F5F5;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
`;

const PasswordText = styled.code`
    flex: 1;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 1px;
`;

const CopyButton = styled.button`
    background: #2196F3;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;

    &:hover {
        background: #1976D2;
    }
`;

const PasswordNote = styled.p`
    font-size: 13px;
    color: #666;
    margin: 0;
`;

// Advanced search styles
const AdvancedSearchSection = styled.div`
    width: 100%;
`;

const AdvancedFiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const AdvancedSearchActions = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
`;

const AdvancedSearchResults = styled.div`
    margin-top: 20px;

    h3 {
        margin: 0 0 16px 0;
        font-size: 18px;
        color: #333;
    }
`;

export default UsersManagementPage;
